/**
 * IPO Data Mapper & Normalizer
 * Maps raw backend API response from GET /api/core/ipos/{ipo_id} into structured view data.
 * Safe against missing/null fields and partial extraction states.
 */

import { buildCompanyNews } from './companyNews';

const BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://ipo-backend-a9sl.onrender.com';

export const mapIpoDetailResponse = (apiData) => {
  if (!apiData) return null;

  // Support both { success: true, data: { ... } } and raw data object
  const root = apiData.data || apiData;
  const json = root.json_data || {};

  const basic = json.basic_company_details || {};
  const ipoStructure = json.ipo_structure || {};
  const capital = json.capital_structure || {};
  const business = json.business_information || {};
  const objects = json.objects_of_issue || {};
  const financial = json.financial_information || {};
  const valuation = json.valuation_information || {};
  const risk = json.risk_and_legal_information || {};
  const derived = json.derived_information || {};
  const market = json.market_information || {};
  const portal = json.portal_narrative || {};

  // Company Name & Basic Info
  const companyName =
    root.company_name ||
    basic.company_name ||
    market.nse_company ||
    '';

  const brandName = root.brand_name || basic.brand_name || '';
  const cin = root.cin || basic.cin || '';
  const type = root.ipo_type || ipoStructure.ipo_type || '';
  const issueType = ipoStructure.ipo_type || '';
  const biddingStatus = market.status || 'Upcoming';

  let exchanges = root.listing_exchange || '';
  if (!exchanges && ipoStructure.listing_exchanges) {
    exchanges = Array.isArray(ipoStructure.listing_exchanges)
      ? ipoStructure.listing_exchanges.join(', ')
      : String(ipoStructure.listing_exchanges);
  }

  // Logo URL normalization
  let logoUrl = basic.logo_url || '';
  if (logoUrl && !logoUrl.startsWith('http')) {
    logoUrl = `${BASE_URL.replace(/\/+$/, '')}/${logoUrl.replace(/^\/+/, '')}`;
  }

  // Pricing & Price Band
  let priceBand = '';
  const floorPrice = valuation.price_band?.floor_price || market.floor_price || '';
  const capPrice = valuation.price_band?.cap_price || market.cap_price || '';
  if (floorPrice && capPrice) {
    priceBand = `₹${floorPrice} – ₹${capPrice}`;
  } else if (market.band_display) {
    priceBand = market.band_display;
  } else if (market.price_raw) {
    priceBand = market.price_raw;
  } else if (capPrice) {
    priceBand = `₹${capPrice}`;
  }

  const upperPrice = capPrice ? Number(capPrice) : null;
  const faceValue = ipoStructure.face_value_per_share
    ? `₹${ipoStructure.face_value_per_share} per share`
    : '';

  // Issue Size
  let totalIssueSize = '';
  if (ipoStructure.fresh_issue_amount) {
    totalIssueSize = `₹${ipoStructure.fresh_issue_amount} Cr`;
  } else if (derived.valuation?.market_cap_post_issue_cr) {
    totalIssueSize = `₹${derived.valuation.market_cap_post_issue_cr} Cr`;
  } else if (ipoStructure.total_issue_size_shares) {
    totalIssueSize = `${ipoStructure.total_issue_size_shares} Shares`;
  }

  const freshIssue = ipoStructure.fresh_issue_amount
    ? `₹${ipoStructure.fresh_issue_amount} Cr`
    : ipoStructure.fresh_issue_shares
    ? `${ipoStructure.fresh_issue_shares} Shares`
    : '';

  const ofs = ipoStructure.offer_for_sale || '';

  // Lot Size & Calculator
  const lotSize = market.lot_size ? Number(market.lot_size) : null;
  const minBidAmount =
    lotSize && upperPrice ? `₹${(lotSize * upperPrice).toLocaleString('en-IN')}` : '';

  // Dates
  const openDate = market.issue_start || ipoStructure.bid_issue_opens || '';
  const closeDate = market.issue_end || ipoStructure.bid_issue_closes || '';
  const listingDate = market.listing_date || '';
  const anchorDate = ipoStructure.anchor_investor_bid_date || '';
  const drhpDate = root.drhp_date || json.drhp_date || '';

  // Construct standard Milestones Timeline
  const datesTimeline = [];
  if (anchorDate) {
    datesTimeline.push({
      label: 'Anchor Bid Date',
      date: anchorDate,
      desc: 'Institutional Allocation',
      color: 'indigo',
    });
  }
  if (openDate) {
    datesTimeline.push({
      label: 'Bidding Starts',
      date: openDate,
      desc: 'Issue Opens for Public',
      color: 'emerald',
    });
  }
  if (closeDate) {
    datesTimeline.push({
      label: 'Bidding Closes',
      date: closeDate,
      desc: 'Final Bid Cut-Off 5 PM',
      color: 'amber',
    });
  }
  if (listingDate) {
    datesTimeline.push({
      label: 'Tentative Listing',
      date: listingDate,
      desc: `Listing on ${exchanges || 'Exchanges'}`,
      color: 'emerald',
    });
  } else if (drhpDate) {
    datesTimeline.push({
      label: 'DRHP Filed Date',
      date: drhpDate,
      desc: 'Draft Red Herring Prospectus',
      color: 'indigo',
    });
  }

  // GMP values
  const gmpRaw = market.gmp;
  const liveGmp =
    gmpRaw !== null && gmpRaw !== undefined && gmpRaw !== ''
      ? `+₹${gmpRaw}`
      : '';
  const gmpPercent =
    market.gmp_percent !== null && market.gmp_percent !== undefined && market.gmp_percent !== ''
      ? `+${market.gmp_percent}%`
      : '';
  const gmpStatus = market.gmp_status || market.status || 'Active';
  const gmpDayHigh = market.gmp_day_high ? `₹${market.gmp_day_high}` : '';
  const gmpDayLow = market.gmp_day_low ? `₹${market.gmp_day_low}` : '';
  const estimatedListingPrice =
    upperPrice && gmpRaw ? `₹${upperPrice + Number(gmpRaw)}` : '';
  const gmpUpdatedOn = market.gmp_updated_on || '';

  // Subscription
  const subscriptionRaw = market.subscription || {};
  const overallSubscription =
    subscriptionRaw.sub ||
    (subscriptionRaw.sub_val ? `${subscriptionRaw.sub_val}x` : '');
  const subscriptionCategories = Array.isArray(subscriptionRaw.by_category)
    ? subscriptionRaw.by_category
    : [];

  // Objects of Issue
  const objectsList = Array.isArray(objects.proposed_utilisation)
    ? objects.proposed_utilisation.map((item, idx) => ({
        id: idx + 1,
        object: item.object || item.purpose || '',
        amount: item.amount ? `₹${item.amount}` : '',
      }))
    : [];

  // Overview / Lead Paragraph
  const summaryParagraph =
    portal.summary_paragraph ||
    business.company_overview ||
    business.primary_business_summary ||
    '';

  const snapshotBullets = Array.isArray(portal.snapshot_bullets)
    ? portal.snapshot_bullets
    : [];

  // Top Risk Factors
  const topRisks = Array.isArray(risk.top_risk_factors)
    ? risk.top_risk_factors.map((r) => (typeof r === 'string' ? r : r.text || ''))
    : [];

  // Litigations
  const litigations = Array.isArray(risk.outstanding_litigations_table)
    ? risk.outstanding_litigations_table
    : [];
  const litigationsSummary = risk.outstanding_litigations_summary || '';

  // Peer Comparison
  const peers = Array.isArray(valuation.peer_comparison)
    ? valuation.peer_comparison
    : [];

  // Promoter Shareholding
  const promoterShareholding = Array.isArray(capital.promoter_shareholding)
    ? capital.promoter_shareholding
    : [];

  // Restated Financials Table
  const restatedSummary = financial.restated_summary || {};
  const revenueObj = restatedSummary.revenue_lakhs || restatedSummary.revenue || {};
  const patObj = restatedSummary.pat_lakhs || restatedSummary.pat || {};
  const financialYears = Array.from(
    new Set([...Object.keys(revenueObj), ...Object.keys(patObj)])
  );

  const financialRows = financialYears.map((fy) => ({
    year: fy.toUpperCase().replace('_', ' '),
    revenue: revenueObj[fy] || '',
    pat: patObj[fy] || '',
  }));

  // Valuation KPIs & Derived Valuation
  const kpisRaw = derived.kpis || {};
  const derivedValuation = derived.valuation || {};
  const epsList = Array.isArray(valuation.eps) ? valuation.eps : [];
  const navPerShare = valuation.nav_per_share_inr || '';

  // Capital Structure
  const preIssueShares = capital.pre_issue_shares || '';
  const postIssueShares = capital.post_issue_shares || '';
  const sharesOffered = capital.shares_offered_in_ipo || '';
  const authorisedCapital = capital.authorised_share_capital || '';
  const promoterNames = Array.isArray(basic.promoter_names) ? basic.promoter_names : [];

  // Reservation Structure
  const resRaw = ipoStructure.reservation_structure || {};
  const reservation = {
    qibMax: resRaw.qib_max_percent || '50',
    niiMin: resRaw.non_institutional_min_percent || '15',
    retailMin: resRaw.retail_min_percent || '35',
    details: resRaw.details || '',
  };

  // Business & Operational Footprint
  const businessModel = business.business_model || '';
  const industrySummary = business.industry_summary || '';
  const manufacturing = business.manufacturing_facility || '';
  const geographicPresence = business.geographic_presence || '';
  const issueDocumentSummary = business.issue_document_summary || '';

  // Multi-Year Financial KPI Matrix
  const kpiMetricLabels = {
    ebitda_margin: 'EBITDA Margin',
    pat_margin_percent: 'PAT Margin',
    roe_percent: 'Return on Equity (ROE)',
    roce_percent: 'Return on Capital Employed (ROCE)',
    ronw_percent: 'Return on Net Worth (RoNW)',
  };

  const kpiYearsSet = new Set();
  Object.values(kpisRaw).forEach((metricObj) => {
    if (typeof metricObj === 'object' && metricObj !== null) {
      Object.keys(metricObj).forEach((fy) => kpiYearsSet.add(fy));
    }
  });
  const kpiYears = Array.from(kpiYearsSet);

  const kpisMatrix = Object.entries(kpisRaw)
    .filter(([key, val]) => typeof val === 'object' && val !== null && Object.keys(val).length > 0)
    .map(([metricKey, metricObj]) => ({
      key: metricKey,
      label: kpiMetricLabels[metricKey] || metricKey.replace(/_/g, ' ').toUpperCase(),
      values: kpiYears.map((fy) => ({
        year: fy.toUpperCase().replace('_', ' '),
        value: metricObj[fy] || '—',
      })),
    }));

  // Extended Valuation Metrics
  const weightedAvgEps = valuation.weighted_average_eps || '';
  const weightedAvgRonw = valuation.weighted_average_ronw
    ? `${valuation.weighted_average_ronw}%`
    : '';
  const peAtFloorPrice = derivedValuation.pe_at_floor_price || '';
  const peAtIssuePrice = derivedValuation.pe_at_issue_price || '';
  const mcapPreIssue = derivedValuation.market_cap_pre_issue_cr
    ? `₹${derivedValuation.market_cap_pre_issue_cr} Cr`
    : '';
  const mcapPostIssue = derivedValuation.market_cap_post_issue_cr
    ? `₹${derivedValuation.market_cap_post_issue_cr} Cr`
    : '';
  const peLowest = valuation.industry_pe_ratio?.lowest || '';
  const peHighest = valuation.industry_pe_ratio?.highest || '';
  const peComposite = valuation.industry_pe_ratio?.industry_composite || '';

  // Registrar & Contact
  const regRaw = basic.registrar || {};
  const registrar = typeof regRaw === 'object' && regRaw !== null ? regRaw : { name: String(regRaw || '') };

  // Lead Manager (BRLM) normalization
  const leadManagerRaw = basic.lead_manager || {};
  let leadManagerObj = {
    name: '',
    contactPerson: '',
    phone: '',
    email: '',
    website: '',
    allLeadManagers: [],
    sebiRegistration: '',
  };

  if (typeof leadManagerRaw === 'string') {
    leadManagerObj.name = leadManagerRaw;
  } else if (typeof leadManagerRaw === 'object' && leadManagerRaw !== null) {
    leadManagerObj.name = leadManagerRaw.name || '';
    leadManagerObj.contactPerson = leadManagerRaw.contact_person || '';
    leadManagerObj.phone = leadManagerRaw.telephone || '';
    leadManagerObj.email = leadManagerRaw.email || '';
    leadManagerObj.website = leadManagerRaw.website || '';
    leadManagerObj.sebiRegistration = leadManagerRaw.sebi_registration || '';
    if (Array.isArray(leadManagerRaw.all_lead_managers)) {
      leadManagerObj.allLeadManagers = leadManagerRaw.all_lead_managers.filter(Boolean);
    }
  }

  // Registered Office & Contact Details normalization
  const regOfficeRaw = basic.registered_office || {};
  let registeredOfficeAddress = '';
  let officeContactPerson = '';
  let contactPhone = basic.contact_phone || '';
  let contactEmail = basic.contact_email || '';
  let companyWebsite = basic.company_website || '';

  if (typeof regOfficeRaw === 'string') {
    registeredOfficeAddress = regOfficeRaw;
  } else if (typeof regOfficeRaw === 'object' && regOfficeRaw !== null) {
    registeredOfficeAddress = regOfficeRaw.address || '';
    if (regOfficeRaw.contact_person) officeContactPerson = regOfficeRaw.contact_person;
    if (regOfficeRaw.telephone && !contactPhone) contactPhone = regOfficeRaw.telephone;
    if (regOfficeRaw.email && !contactEmail) contactEmail = regOfficeRaw.email;
    if (regOfficeRaw.website && !companyWebsite) companyWebsite = regOfficeRaw.website;
  }

  return {
    id: root.id,
    companyName,
    brandName,
    cin,
    type,
    issueType,
    biddingStatus,
    exchanges,
    logoUrl,
    tagline: summaryParagraph,
    symbol: market.nse_symbol || '',

    // Pricing & Metrics
    priceBand,
    floorPrice,
    upperPrice,
    faceValue,
    totalIssueSize,
    freshIssue,
    ofs,
    lotSize,
    minBidAmount,

    // Dates
    openDate,
    closeDate,
    tentativeListing: listingDate || 'To be announced',
    closeDateText: closeDate ? `Bidding Closes ${closeDate}` : '',
    datesTimeline,

    // GMP
    liveGmp,
    gmpPercent,
    gmpStatus,
    gmpDayHigh,
    gmpDayLow,
    estimatedListingPrice,
    gmpUpdatedOn,

    // Subscription
    overallSubscription,
    subscriptionCategories,

    // Sections
    summaryParagraph,
    snapshotBullets,
    founderQuote: portal.founder_quote || '',
    pressHighlights: Array.isArray(portal.press_highlights) ? portal.press_highlights : [],
    objectsSummary: objects.objects_summary || '',
    objectsList,
    companyOverview: business.company_overview || '',
    businessModel,
    industrySummary,
    productCategories: Array.isArray(business.product_categories)
      ? business.product_categories
      : [],
    geographicPresence,
    manufacturing,
    issueDocumentSummary,

    // Capital & Reservation Structure
    preIssueShares,
    postIssueShares,
    sharesOffered,
    authorisedCapital,
    promoterNames,
    reservation,

    // Risks & Legal
    topRisks,
    litigations,
    litigationsSummary,
    groupCompanies: Array.isArray(risk.group_companies) ? risk.group_companies : [],

    // Peers & Promoters
    peers,
    promoterShareholding,

    // Financials & KPIs
    financialUnit: financial.unit || '₹ Lakhs',
    revenueCagr: financial.revenue_cagr || '',
    financialRows,
    kpis: kpisRaw,
    kpiYears,
    kpisMatrix,
    derivedValuation,
    epsList,
    navPerShare,
    industryPe: valuation.industry_pe_ratio || {},

    // Extended Valuation
    weightedAvgEps,
    weightedAvgRonw,
    peAtFloorPrice,
    peAtIssuePrice,
    mcapPreIssue,
    mcapPostIssue,
    peLowest,
    peHighest,
    peComposite,

    // Contacts
    registrar,
    leadManager: leadManagerObj.name,
    leadManagerObj,
    contactEmail,
    contactPhone,
    registeredOffice: registeredOfficeAddress,
    officeContactPerson,
    companyWebsite,
    incorporationDate: basic.incorporation_date || '',

    // Press coverage the backend collected, minus its false matches
    companyNews: buildCompanyNews(basic, companyName),

    // Raw JSON data for fallback
    raw: root,
  };
};

export default mapIpoDetailResponse;
